import styled from '@emotion/styled';
import { colors } from 'Constants/Colors';
import { palette } from 'Constants/Palette';
import { MexlogImageFallback } from 'Images/index';

/**
 * Cuánto se abre la banda de la descripción en hover. La banda es absoluta y cuelga del
 * borde superior del panel blanco, así que no ocupa espacio en el flujo: el alto del card
 * no cambia al abrirse, y el `overflow: hidden` del PCard recorta la banda si superara
 * al thumbnail.
 * ponytail: techo consciente. La descripción vive en una caja de alto fijo y se recorta
 * con -webkit-line-clamp; una descripción larga se corta. Medir el texto en JS solo si
 * algún día se exige mostrarla completa sin límite.
 */
const DESC_REVEAL_PX = 84;
const BODY_PADDING_X_PX = 16;
const REVEAL_TRANSITION = '0.28s ease';

export const PCard = styled.div`
  display: flex;
  flex-direction: column;
  background: ${colors.WHITE};
  border: 1px solid ${palette.border};
  border-radius: 14px;
  overflow: hidden;
  cursor: pointer;
  transition:
    transform 0.25s ease,
    box-shadow 0.25s ease,
    border-color 0.25s ease;

  &:hover {
    transform: translateY(-3px) scale(1.02);
    border-color: ${palette.green};
    box-shadow: 0 10px 24px rgba(18, 33, 26, 0.12);
  }

  &:hover .pcard-thumb {
    transform: scale(1.08);
  }

  /* Las dos reglas de abajo solo muerden en la variante extendida: sus clases las
     ponen la descripción y el estatus, que el card del Dashboard no recibe. */
  &:hover .pcard-desc {
    max-height: ${DESC_REVEAL_PX}px;
  }

  &:hover .pcard-status {
    left: 50%;
    transform: translateX(-50%);
  }
`;

// position: relative solo ancla los overlays de la variante extendida;
// sin ellos el layout es idéntico al de la variante mínima.
export const ThumbWrap = styled.div`
  position: relative;
  height: 180px;
  overflow: hidden;
`;

export const Thumb = styled.div<{ imageUrl: string | null }>`
  width: 100%;
  height: 100%;
  transition: transform 0.3s ease;
  background: ${({ imageUrl }) => `url(${imageUrl || MexlogImageFallback}) center / cover no-repeat`};
`;

/**
 * Anclado a la derecha, pero expresado con `left` para que el viaje al centro interpole:
 * `left: 100%` + `translateX(calc(-100% - 10px))` es pixel a pixel lo mismo que
 * `right: 10px`, y ambas propiedades sí animan hacia `left: 50%` + `translateX(-50%)`.
 * Con `right: 10px` el navegador tendría que interpolar contra `right: auto` y saltaría.
 */
export const StatusSlot = styled.div`
  position: absolute;
  top: 10px;
  left: 100%;
  transform: translateX(calc(-100% - 10px));
  transition:
    left ${REVEAL_TRANSITION},
    transform ${REVEAL_TRANSITION};
`;

/**
 * Editar del caso admin: misma esquina que el botón del CourseCard legacy (arriba a la
 * derecha), pero dentro del thumbnail — el PCard recorta con overflow: hidden, así que
 * no puede colgar fuera del card como el legacy. Comparte esquina con StatusSlot solo
 * en teoría: ningún consumidor pasa status y onEdit a la vez (admin no tiene estatus
 * de usuario).
 */
export const EditButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: ${palette.green};
  color: ${colors.WHITE};
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(18, 33, 26, 0.25);
  transition: background 0.2s ease;

  &:hover {
    background: ${palette.ink};
  }
`;

// Esquina superior izquierda: libre de StatusSlot (arriba derecha, viaja al centro en
// hover), de CategoryChip (abajo izquierda) y del botón de editar (arriba derecha).
export const DraftBadge = styled.span`
  position: absolute;
  top: 10px;
  left: 10px;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  color: ${colors.WHITE};
  background: ${colors.BLACK_50};
`;

export const CategoryChip = styled.span<{ chipColor?: string }>`
  position: absolute;
  bottom: 10px;
  left: 10px;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  color: ${colors.WHITE};
  background: ${({ chipColor }) => chipColor || palette.green};
`;

// position: relative ancla la banda de la descripción a su borde superior;
// background propio para que el panel y la banda se lean como un solo blanco continuo.
export const Body = styled.div`
  position: relative;
  background: ${colors.WHITE};
  padding: 14px ${BODY_PADDING_X_PX}px 16px;
`;

export const Name = styled.p`
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  color: ${palette.ink};
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

/**
 * Fuera del flujo (absoluta) y colgada del borde superior del Body: al abrirse no le suma
 * alto a nadie, solo se despliega hacia arriba tapando el thumbnail con su propio blanco.
 * Mismo padding lateral que el Body para que ambos se lean como un panel continuo.
 */
export const DescReveal = styled.div`
  position: absolute;
  bottom: 100%;
  left: 0;
  right: 0;
  box-sizing: border-box;
  padding: 0 ${BODY_PADDING_X_PX}px;
  background: ${colors.WHITE};
  max-height: 0;
  overflow: hidden;
  transition: max-height ${REVEAL_TRANSITION};
`;

export const Desc = styled.p`
  margin: 8px 0 0;
  font-size: 13px;
  line-height: 1.45;
  color: ${palette.muted};
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
`;

export const Cm = styled.div`
  margin-top: 4px;
  font-size: 13px;
  color: ${palette.muted};
`;

export const DueLabel = styled.span`
  display: block;
  margin-top: 10px;
  font-size: 13px;
  color: ${palette.muted};
`;

export const Creator = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid ${palette.borderSoft};
  font-size: 13px;
  color: ${palette.muted};
`;

const avatar = `
  width: 24px;
  height: 24px;
  border-radius: 50%;
  flex-shrink: 0;
`;

export const CreatorPhoto = styled.img`
  ${avatar}
  object-fit: cover;
`;

export const CreatorInitial = styled.span`
  ${avatar}
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${palette.track};
  color: ${palette.green};
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
`;

export const CreatorName = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
